import { useState, useEffect, useCallback, useRef } from "react";
import { useRecordingSessions } from "./useRecordingSessions";
import { useRecorderManagement } from "./useRecorderManagement";
import { useRecordingLifecycle } from "./useRecordingLifecycle";
import { ConsultationService } from "../services/consultationService";
import { idbGet, idbPut, idbDelete, idbKeys } from "@utils/idb";

export const useConsultationRecording = (
  backendUrl,
  busyState,
  consultationManager,
  uploadQueue
) => {
  // State
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [audioURL, setAudioURL] = useState(null);
  const [processingStage, setProcessingStage] = useState("idle");

  // Refs and services
  const mountedRef = useRef(true);
  const consultationService = new ConsultationService(backendUrl);
  const sessions = useRecordingSessions();
  const { begin, end } = busyState;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Cleanup any leftover sessions
      for (const session of sessions.sessionsRef.current.values()) {
        lifecycle.stopStreamTracks(session.stream);
      }
    };
  }, [sessions.sessionsRef]);

  // Upload segment logic
  const uploadSegment = useCallback(
    async (blob, { isFinal = false } = {}, sessionId) => {
      const session = sessions.getSession(sessionId);
      if (!session) return;

      try {
        const result = await consultationService.uploadSegment(blob, isFinal, {
          sessionId,
          segmentIndex: session.nextIndex,
        });
        const transcription = result?.data?.transcription;
        if (transcription) session.transcriptionParts.push(transcription);
        return result;
      } catch (err) {
        console.error("Segment upload failed:", err);
      }
    },
    [consultationService, sessions]
  );
  const recorderManager = useRecorderManagement(
    sessions,
    uploadSegment,
    {
      onFinalize: ({ sessionId, session, segBlob }) =>
        lifecycle.finalizeRecording(
          sessionId,
          session,
          segBlob,
          setProcessingStage,
          setAudioURL,
          audioURL,
          setIsRecording,
          setIsPaused
        ),
      onReset: ({ sessionId, session }) =>
        lifecycle.handleSessionReset(sessionId, session, audioURL, setAudioURL),
    },
    uploadQueue
  );

  const continueRecording = useCallback(
    async ({ recordingId, createdAtISO }) => {
      if (isRecording) return;
      // Make this the active session (and create minimal manifest if needed)
      const session = await sessions.ensureSession(recordingId, createdAtISO);
      try {
        const manifest = (await idbGet("sessions", recordingId)) || {};
        manifest.isContinuation = true;
        manifest.recordingId = recordingId;
        await idbPut("sessions", recordingId, manifest);
      } catch {}
      // Get mic and start a recorder attached to this session
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      session.stream = stream;
      const mr = recorderManager.createRecorder(stream, recordingId);
      session.recorders.add(mr);

      setIsRecording(true);
      setIsPaused(false);
      setProcessingStage("recording");
      begin("recording");
    },
    [
      isRecording,
      sessions.ensureSession,
      recorderManager,
      setIsRecording,
      setIsPaused,
      setProcessingStage,
      begin,
    ]
  );

  const emergencyFlushAndPersist = useCallback(async () => {
    const sessionId = sessions.currentSessionIdRef.current;
    if (!sessionId) {
      console.log("🔄 No active session for emergency flush");
      return;
    }

    const session = sessions.getSession(sessionId);
    if (!session) {
      console.log("🔄 Session not found for emergency flush:", sessionId);
      return;
    }

    // Mark session for emergency processing IMMEDIATELY
    session.emergencyProcessing = true;
    session.emergencyFlush = true;
    session.isFinal = true;

    // SYNC: Immediately update IndexedDB before anything else
    try {
      const manifest = (await idbGet("sessions", sessionId)) || {
        sessionId,
        createdAtISO: session.createdAtISO || new Date().toISOString(),
        segments: [],
        transcriptionParts: [],
        status: "emergency_flush",
        emergencyFlush: true,
        isFinal: true,
        lastSegmentIsFinal: true,
        completed: false,
      };

      manifest.emergencyFlush = true;
      manifest.isFinal = true;
      manifest.lastSegmentIsFinal = true;
      manifest.status = "emergency_flush";

      // CRITICAL: Use await to ensure this writes before page closes
      await idbPut("sessions", sessionId, manifest);
      console.log("✅ Emergency session marked as final in IndexedDB");

      // Force create processing placeholder if doesn't exist
      const hasPlaceholder = consultationManager.pendingGroups?.some((g) =>
        g.files?.some((f) => f.recording_id === sessionId && f.isPlaceholder)
      );

      if (!hasPlaceholder) {
        consultationManager.addProcessingPlaceholder(
          sessionId,
          manifest.createdAtISO
        );
      }
    } catch (error) {
      console.error("❌ Failed to persist emergency session:", error);
    }

    // Now force flush all recorders
    try {
      const flushPromises = Array.from(session.recorders).map(async (mr) => {
        try {
          if (mr.state === "recording" || mr.state === "paused") {
            mr.requestData();
          }
          await new Promise((resolve) => setTimeout(resolve, 50));
          mr.stop();
        } catch (error) {
          console.warn("Recorder flush error:", error);
        }
      });

      await Promise.allSettled(flushPromises);

      // Extra time for IndexedDB writes to complete
      await new Promise((resolve) => setTimeout(resolve, 200));
    } catch (error) {
      console.error("❌ Emergency flush error:", error);
    } finally {
      session.emergencyProcessing = false;
    }
  }, [sessions, consultationManager]);

  const lifecycle = useRecordingLifecycle(
    sessions,
    consultationService,
    busyState,
    consultationManager
  );

  // Audio URL cleanup effect
  useEffect(() => {
    return () => {
      if (audioURL) URL.revokeObjectURL(audioURL);
    };
  }, [audioURL]);

  // Recording controls
  const startRecording = useCallback(async () => {
    if (isRecording) return;
    const existingSessions = await idbKeys("sessions");
    const incompleteSessions = [];

    for (const sessionId of existingSessions) {
      const manifest = await idbGet("sessions", sessionId);
      if (manifest && !manifest.completed && !manifest.emergencyFlush) {
        incompleteSessions.push({ sessionId, manifest });
      }
    }

    // If we have incomplete sessions, resume the most recent one
    if (incompleteSessions.length > 0) {
      const mostRecent = incompleteSessions.sort(
        (a, b) =>
          new Date(b.manifest.createdAtISO) - new Date(a.manifest.createdAtISO)
      )[0];

      console.log("🔄 Resuming existing session:", mostRecent.sessionId);
      sessions.currentSessionIdRef.current = mostRecent.sessionId;
      // Rehydrate session state if needed
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const { sessionId, session } = sessions.createSession(stream);

      const mr = recorderManager.createRecorder(stream, sessionId);
      session.recorders.add(mr);

      setIsRecording(true);
      setIsPaused(false);
      setProcessingStage("recording");
      begin("recording");
    } catch (err) {
      console.error("Error accessing microphone:", err);
      setProcessingStage("error");
    }
  }, [isRecording, sessions, recorderManager, begin]);

  const finishRecording = useCallback(() => {
    const sessionId = sessions.currentSessionIdRef.current;
    if (!sessionId) return;
    const session = sessions.getSession(sessionId);
    if (!session) return;

    consultationManager.addProcessingPlaceholder(
      sessionId,
      session.createdAtISO
    );
    session.skipUpload = false;

    if (!session.uploadingStarted) {
      session.uploadingStarted = true;
      setProcessingStage("uploading");
      begin("uploading");
    }

    setIsRecording(false);
    setIsPaused(false);
    recorderManager.stopAllRecorders(sessionId);
  }, [sessions, consultationManager, recorderManager, begin]);

  const resetRecording = useCallback(async () => {
    const sessionId = sessions.currentSessionIdRef.current;
    if (!sessionId) return;
    const session = sessions.getSession(sessionId);
    if (!session || session.resetPending) return;

    consultationManager.removeProcessingPlaceholder(sessionId);
    session.resetPending = true;
    session.skipUpload = true;

    setIsRecording(false);
    setIsPaused(false);
    setProcessingStage("idle");

    if (session.uploadingStarted) {
      session.uploadingStarted = false;
      end("uploading");
    }

    const hadLive = session.recorders.size > 0;
    // 🆕 COMPLETE STORAGE CLEANUP FUNCTION
    const cleanupFromStorage = async () => {
      try {
        console.log("🧹 Cleaning up session from storage:", sessionId);

        // 1. Get the manifest first to know what segments exist
        const manifest = await idbGet("sessions", sessionId);

        // 2. Delete all audio segments
        if (manifest?.segments) {
          for (const seg of manifest.segments) {
            await idbDelete("segments", seg.id);
            console.log("✅ Deleted segment:", seg.id);
          }
        }

        // 3. Delete the session manifest
        await idbDelete("sessions", sessionId);
        console.log("✅ Deleted session manifest:", sessionId);
      } catch (error) {
        console.error("❌ Storage cleanup failed:", error);
      }
    };

    if (!hadLive) {
      if (audioURL) URL.revokeObjectURL(audioURL);
      setAudioURL(null);
      end("recording");
      lifecycle.stopStreamTracks(session.stream);
      await cleanupFromStorage();
      sessions.deleteSession(sessionId);
      return;
    }

    recorderManager.stopAllRecorders(sessionId);
  }, [
    sessions,
    consultationManager,
    recorderManager,
    audioURL,
    end,
    lifecycle.stopStreamTracks,
  ]);

  const pauseRecording = useCallback(() => {
    const sessionId = sessions.currentSessionIdRef.current;
    const mr = recorderManager.pauseLatestRecorder(sessionId);
    if (mr) setIsPaused(true);
  }, [sessions, recorderManager]);

  const resumeRecording = useCallback(() => {
    const sessionId = sessions.currentSessionIdRef.current;
    const mr = recorderManager.resumeLatestRecorder(sessionId);
    if (mr) setIsPaused(false);
  }, [sessions, recorderManager]);

  // Derived state
  const micVisualState = isRecording
    ? isPaused
      ? "paused"
      : "recording"
    : "idle";

  return {
    // State
    isRecording,
    isPaused,
    audioURL,
    processingStage,
    micVisualState,

    // Actions
    startRecording,
    finishRecording,
    resetRecording,
    pauseRecording,
    resumeRecording,
    setProcessingStage,
    // expose for lifecycle callbacks
    setAudioURL,
    setIsRecording,
    setIsPaused,
    emergencyFlushAndPersist,
    continueRecording,
  };
};
